import actions from 'actions'
import { ROUTES } from 'consts'
import { Roles } from 'consts/authConsts'
import useFeatureFlags from 'hooks/useFeatureFlags'
import useQueryParams from 'hooks/useQueryParams'
import { useEffect, useMemo, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { useHistory } from 'react-router-dom/cjs/react-router-dom.min'
import { useFetchCopilotMetabaseDataQuery } from 'store/modules/CopilotControl'
import { isUserOfRole } from 'utils/authUtils'
import useCopilotAccountOwnerId from './useCopilotAccountOwnerId'

const copilotDataToMetabaseQuestion = {
  uniqueScans: 3738,
  needsAttentionCount: 3739,
  patientsContacted: 3740,
  patientsContactedForBrushing: 3745,
  patientsContactedForChewies: 3746
}

const AverageCheckUpTimeMinutes = 5

export default () => {
  const dispatch = useDispatch()
  const history = useHistory()
  const { copilotControl: copilotControlFF } = useFeatureFlags()
  const accountOwnerId = useCopilotAccountOwnerId()
  const { doctor } = useSelector(state => state.profileReducer)
  const { queryParams } = useQueryParams()
  const isDevAdmin = isUserOfRole([Roles.DevAdmin])

  const { data: practiceGuidelinesData, isLoading: isLoadingPracticeGuidelines } = useSelector(
    state => state.practiceReducer.hiPracticeGuidelines
  )

  // Default to 30 days
  const [pastXDays, setPastXDays] = useState(30)

  const {
    data = {},
    isFetching: isLoading = true,
    isUninitialized = true
  } = useFetchCopilotMetabaseDataQuery({ accountOwnerId, pastXDays }, { skip: !accountOwnerId })

  const uniqueScansCount = useMemo(() => {
    return data[copilotDataToMetabaseQuestion.uniqueScans]
  }, [data])

  const patientsContactedCount = useMemo(() => {
    return data[copilotDataToMetabaseQuestion.patientsContacted]
  }, [data])

  const patientsContactedForBrushingCount = useMemo(() => {
    return data[copilotDataToMetabaseQuestion.patientsContactedForBrushing]
  }, [data])

  const patientsContactedForChewiesCount = useMemo(() => {
    return data[copilotDataToMetabaseQuestion.patientsContactedForChewies]
  }, [data])

  const needsAttentionCount = useMemo(() => {
    return data[copilotDataToMetabaseQuestion.needsAttentionCount]
  }, [data])

  const timeSavedMinutes = useMemo(() => {
    return uniqueScansCount * AverageCheckUpTimeMinutes
  }, [uniqueScansCount])

  const noAttentionRequiredRate = useMemo(() => {
    return !uniqueScansCount || uniqueScansCount === 0 || needsAttentionCount > uniqueScansCount
      ? null
      : `${Math.ceil((1 - needsAttentionCount / uniqueScansCount) * 100)}%`
  }, [uniqueScansCount, needsAttentionCount])

  const practiceGuidelinesConfiguration = useMemo(() => {
    return Object.entries(JSON.parse(practiceGuidelinesData?.categories || '{}')).map(([categoryKey, data]) => ({
      categoryKey,
      isEnabled: data.isEnabled
    }))
  }, [practiceGuidelinesData])

  useEffect(() => {
    if (!practiceGuidelinesData && accountOwnerId) {
      dispatch(actions.fetchPracticeGuidelines({ doctorId: accountOwnerId }))
    }
  }, [dispatch, practiceGuidelinesData, accountOwnerId])

  useEffect(() => {
    if (isDevAdmin && queryParams.copilotDoctorId) {
      return
    }

    if (doctor?.id && !copilotControlFF) {
      history.push(ROUTES.PATIENTS)
    }
  }, [isDevAdmin, queryParams, history, copilotControlFF, doctor?.id])

  return {
    pastXDays,
    setPastXDays,
    isLoading,
    isUninitialized,
    timeSavedMinutes,
    totalScans: uniqueScansCount,
    totalNeedsAttention: needsAttentionCount,
    virtualCheckups: uniqueScansCount,
    noAttentionRequired: noAttentionRequiredRate,
    patientsWeContacted: patientsContactedCount,
    patientsWeSentBrushing: patientsContactedForBrushingCount,
    patientsWeSentChewies: patientsContactedForChewiesCount,
    practiceGuidelinesConfiguration,
    isLoadingPracticeGuidelines
  }
}
